import React from 'react';
import { StatusBadge, StatusBadgeProps } from './StatusBadge';
import styles from './StatusBadgeGroup.module.css';

export interface StatusBadgeGroupProps {
  /** Array of badge configurations to render */
  badges: (StatusBadgeProps | null | undefined | false)[];
  /** Gap between badges in pixels */
  gap?: number;
  /** Additional CSS class name */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
}

/**
 * A component for displaying multiple StatusBadge components in a row.
 * Automatically filters out null/undefined/false values from the badges array.
 * 
 * @example
 * <StatusBadgeGroup
 *   badges={[
 *     { label: 'Successful', colorVariant: 'success' },
 *     { label: 'Offline', colorVariant: 'neutral' },
 *     isAmbassador && { label: 'Ambassador', colorVariant: 'accent' }
 *   ]}
 * />
 */
export const StatusBadgeGroup: React.FC<StatusBadgeGroupProps> = ({
  badges,
  gap = 8,
  className = '',
  style = {}
}) => {
  // Filter out falsy values (null, undefined, false)
  const validBadges = badges.filter((badge): badge is StatusBadgeProps => Boolean(badge));

  if (validBadges.length === 0) {
    return null;
  }

  return (
    <div
      className={`${styles.statusBadgeGroup} ${className}`.trim()}
      style={{ gap: `${gap}px`, ...style }}
    >
      {validBadges.map((badgeProps, index) => (
        <StatusBadge key={badgeProps.testId || `badge-${index}`} {...badgeProps} />
      ))}
    </div>
  );
};

export default StatusBadgeGroup;
